import {useEffect,useId,useRef,useState} from 'react';
import {ChevronDown} from 'lucide-react';
import {clampScriptZoom,MIN_SCRIPT_ZOOM,MAX_SCRIPT_ZOOM,SCRIPT_ZOOM_PRESETS} from '@/utils/scriptZoom';

export default function ScriptZoomControl({value,onChange}:{value:number;onChange:(value:number)=>void}){
 const id=useId(),[draft,setDraft]=useState(`${value}%`),[notice,setNotice]=useState('');
 const [open,setOpen]=useState(false),[active,setActive]=useState(-1);
 const root=useRef<HTMLDivElement>(null),input=useRef<HTMLInputElement>(null),list=useRef<HTMLUListElement>(null);
 useEffect(()=>{setDraft(`${value}%`);},[value]);
 useEffect(()=>{
  const outside=(event:PointerEvent)=>{if(!root.current?.contains(event.target as Node))setOpen(false);};
  document.addEventListener('pointerdown',outside);return()=>document.removeEventListener('pointerdown',outside);
 },[]);
 useEffect(()=>{
  const menu=list.current,option=menu?.children[active] as HTMLElement|undefined;
  if(!open||!menu||!option)return;
  if(option.offsetTop<menu.scrollTop)menu.scrollTop=option.offsetTop;
  else if(option.offsetTop+option.offsetHeight>menu.scrollTop+menu.clientHeight)menu.scrollTop=option.offsetTop+option.offsetHeight-menu.clientHeight;
 },[open,active]);
 const choose=(next:number)=>{
  setDraft(`${next}%`);setNotice('');setOpen(false);setActive(-1);
  if(next!==value)onChange(next);
 };
 const commit=()=>{
  const text=draft.trim(),raw=Number(text.replace(/\s*%$/,''));
  if(!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)\s*%?$/.test(text)||!Number.isFinite(raw)){
   setDraft(`${value}%`);setNotice(`Enter a number from ${MIN_SCRIPT_ZOOM} to ${MAX_SCRIPT_ZOOM}%.`);setOpen(false);return;
  }
  const next=clampScriptZoom(raw);setDraft(`${next}%`);setOpen(false);setActive(-1);
  setNotice(raw<MIN_SCRIPT_ZOOM||raw>MAX_SCRIPT_ZOOM?`Zoom limited to ${MIN_SCRIPT_ZOOM}–${MAX_SCRIPT_ZOOM}%.`:'');
  if(next!==value)onChange(next);
 };
 return <div ref={root} className="relative flex shrink-0 items-center gap-1.5 text-xs" role="group" aria-label="Script text zoom"
  onBlur={event=>{if(!event.currentTarget.contains(event.relatedTarget)){commit();setOpen(false);}}}>
  <label htmlFor={`${id}-zoom`}>Zoom</label>
  <div className="flex h-8 w-28 items-center rounded-md border bg-background focus-within:ring-2 focus-within:ring-ring">
   <input ref={input} id={`${id}-zoom`} type="text" inputMode="decimal" autoComplete="off" spellCheck={false} value={draft}
    role="combobox" aria-label="Script zoom percentage" aria-autocomplete="list" aria-expanded={open} aria-controls={`${id}-presets`}
    aria-activedescendant={open&&active>=0?`${id}-option-${active}`:undefined} aria-describedby={`${id}-limits`}
    className="h-full min-w-0 flex-1 rounded-l-md bg-transparent px-2 text-right outline-none"
    onFocus={event=>event.currentTarget.select()}
    onChange={event=>{setDraft(event.target.value);setNotice('');setActive(-1);}}
    onKeyDown={event=>{
     if(event.key==='ArrowDown'||event.key==='ArrowUp'){
      event.preventDefault();setNotice('');const down=event.key==='ArrowDown',selected=(SCRIPT_ZOOM_PRESETS as readonly number[]).indexOf(value);
      setActive(index=>!open?(selected<0?(down?0:SCRIPT_ZOOM_PRESETS.length-1):selected):index<0?(down?0:SCRIPT_ZOOM_PRESETS.length-1):(index+(down?1:-1)+SCRIPT_ZOOM_PRESETS.length)%SCRIPT_ZOOM_PRESETS.length);setOpen(true);
     }else if(open&&(event.key==='Home'||event.key==='End')){event.preventDefault();setActive(event.key==='Home'?0:SCRIPT_ZOOM_PRESETS.length-1);}
     else if(event.key==='Enter'){event.preventDefault();if(open&&active>=0)choose(SCRIPT_ZOOM_PRESETS[active]);else commit();}
     else if(event.key==='Escape'){event.preventDefault();setOpen(false);setActive(-1);setDraft(`${value}%`);setNotice('');}
    }}/>
   <button type="button" className="flex h-full w-7 shrink-0 items-center justify-center rounded-r-md hover:bg-muted"
    aria-label="Show zoom presets" aria-haspopup="listbox" aria-expanded={open} aria-controls={`${id}-presets`}
    onMouseDown={event=>event.preventDefault()} onClick={()=>{setNotice('');setActive((SCRIPT_ZOOM_PRESETS as readonly number[]).indexOf(value));setOpen(current=>!current);input.current?.focus();}}>
    <ChevronDown aria-hidden="true" className="h-3.5 w-3.5"/>
   </button>
  </div>
  {open&&<ul ref={list} id={`${id}-presets`} role="listbox" aria-label="Script zoom presets" className="editor-themed-scroll absolute right-0 top-full z-50 mt-1 max-h-72 w-28 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
   {SCRIPT_ZOOM_PRESETS.map((percent,index)=><li key={percent} id={`${id}-option-${index}`} role="option" aria-selected={value===percent}
    className={`cursor-pointer rounded-sm px-2 py-1.5 ${active===index?'bg-muted':''}`} onPointerMove={()=>setActive(index)}
    onMouseDown={event=>event.preventDefault()} onClick={()=>choose(percent)}>{percent}%</li>)}
  </ul>}
  <span id={`${id}-limits`} className="sr-only">Script text only. {MIN_SCRIPT_ZOOM} to {MAX_SCRIPT_ZOOM} percent. Type a percentage or choose a preset. Press Enter or leave the field to apply.</span>
  {notice&&<span role="status" className="absolute right-0 top-full z-50 mt-1 w-52 rounded-md border bg-popover px-2 py-1.5 text-popover-foreground shadow-md">{notice}</span>}
 </div>;
}
